import { updateSetting, useSession } from '@ltvco/refresh-lib/v1';
import { useRecentReports } from '@ltvco/refresh-lib/v2';
import { useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';

const REPORTS_RUN_THRESHOLD = 4;

// Read the param directly off window.location instead of waiting on
// useQueryParam's effect. On the cold full-page boot Core sends production
// logins through, App.tsx has its own independent useQueryParam subscription
// to this same key (for GA tracking), and effect ordering between the two
// isn't guaranteed.
function readIsCoreLoginRedirectFromLocation(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    new URLSearchParams(window.location.search).get(
      'is_core_login_redirect',
    ) === 'true'
  );
}

// Capture at module load, before React renders. On production cold boot,
// ThemeWrapper/Growthbook remount cycle strips is_core_login_redirect from
// the URL before TrustpilotLoginPromptGate reaches a stable mount, so a
// component-mount read (including useState lazy init) would always see false.
const isCoreLoginRedirectAtBoot = readIsCoreLoginRedirectFromLocation();

export const useTrustpilotLoginPrompt = () => {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const {
    session: { account, isAuthenticated },
  } = useSession();
  const { data: recentReportsData } = useRecentReports({});
  const reportsRun =
    recentReportsData?.meta?.report_quantities?.total_reports_count ?? 0;
  // Covers the legacy/SSO redirect-based login paths: Core does a full-page
  // redirect back to the app with this param, so the SPA remounts already
  // authenticated and the isAuthenticated transition below never fires.
  const [isCoreLoginRedirect, setIsCoreLoginRedirect] = useQueryParam(
    'is_core_login_redirect',
    StringParam,
  );

  // This component is mounted once for the life of the tab, so a ref
  // reliably captures the previous isAuthenticated value across logout/login
  // cycles, letting us detect a fresh login as a false -> true transition —
  // the same signal every other authenticated feature already trusts.
  const wasAuthenticatedRef = useRef(isAuthenticated);
  const [justLoggedIn, setJustLoggedIn] = useState(isCoreLoginRedirectAtBoot);
  const [hasShownThisLogin, setHasShownThisLogin] = useState(false);

  useEffect(() => {
    if (!wasAuthenticatedRef.current && isAuthenticated) {
      setJustLoggedIn(true);
      setHasShownThisLogin(false);
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (isCoreLoginRedirect === 'true') {
      setJustLoggedIn(true);
      setHasShownThisLogin(false);
      // Consume once, then strip from the URL — otherwise a reload, or
      // anyone revisiting a bookmarked/shared link carrying this param,
      // would keep re-marking an existing session as a fresh login.
      setIsCoreLoginRedirect(undefined, 'replaceIn');
    }
  }, [isCoreLoginRedirect, setIsCoreLoginRedirect]);

  const isEligible =
    account?.account?.user_info?.trustpilot_login_prompt_eligible === true;
  const hasConverted =
    account?.account?.user_settings?.hide_trustpilot_login_prompt === true;
  const previouslyDemonstrated =
    account?.account?.user_settings?.hide_experience_survey_modal == true;

  // Same restricted-state checks AuthenticatedRoute/UpgradeLimitModalGate use:
  // these users authenticate normally, so justLoggedIn still fires even though
  // they land on a restricted page right after login. Recycling is
  // intentionally NOT part of isRestrictedAccountState (OAR-2428 QA) -- those
  // users can use the app and reach the Dashboard normally, so the login
  // prompt should behave the same as for any other active user. It's instead
  // excluded specifically from the threshold prompt below (OAR-2445), since
  // Recycling users can't run reports so that trigger is meaningless for them.
  const isNoncompliant = account?.account?.user_info?.noncompliant === true;
  const isInRecycling = account?.account?.user_info?.user_in_recycling === true;
  const renewalDate = new Date(
    account?.account?.subscription_info?.normalize_date ?? '',
  );
  // Same route check as UpgradeLimitModalGate's isReactivationPath. Notification
  // type + renewal date only covers the past-due entry point; a paused account
  // (account.meta.subscription_state === 'paused', see DashboardParent's
  // reactivation snackbar CTA) lands on /reactivation without setting either.
  const isOnReactivationRoute =
    pathname === '/reactivation' || pathname.startsWith('/reactivation/');
  const isInReactivation =
    (account?.account?.notification?.type === 'reactivation' &&
      renewalDate.getTime() < Date.now() &&
      !isNoncompliant) ||
    isOnReactivationRoute;
  // Same list AuthenticatedRoute uses to gate the noncompliant redirect.
  // markUserComplianceComplete/useMarkComplianceRecertificationOnConfirmation
  // flip the noncompliant flag as soon as the confirmation page renders --
  // before the user clicks "Take me back" -- so the flag alone isn't enough
  // to keep the prompt blocked until the Dashboard is actually shown (OAR-2428 QA).
  const complianceRoutes = [
    '/compliance-form',
    '/noncompliance',
    '/compliance-prohibited',
    '/compliance-permitted',
    '/compliance-recomply',
  ];
  const isOnComplianceRoute = complianceRoutes.includes(pathname);
  const isOnLoginRoute =
    pathname === '/login' || pathname.startsWith('/login/');
  const isRestrictedAccountState =
    isNoncompliant || isOnComplianceRoute || isInReactivation;

  // A user who hasn't converted yet also gets a second chance once they've
  // run enough reports to show real engagement, independent of the
  // login-transition trigger above (OAR-2428 follow-up).
  const hasReachedReportThreshold = reportsRun >= REPORTS_RUN_THRESHOLD;

  const commonGates = !isRestrictedAccountState && !isOnLoginRoute;

  const shouldShowLoginPrompt =
    justLoggedIn &&
    isEligible &&
    !hasShownThisLogin &&
    !hasConverted &&
    commonGates;
  const shouldShowThresholdPrompt =
    hasReachedReportThreshold &&
    !previouslyDemonstrated &&
    !isInRecycling &&
    commonGates;

  const shouldShowPrompt = shouldShowLoginPrompt || shouldShowThresholdPrompt;

  // shouldShowThresholdPrompt flips false the instant hasShownThisLogin
  // flips true below, so the Gate can't read it live once the modal is
  // open -- latch which trigger fired so onLeaveReview can still tell them
  // apart while the user is interacting with the modal (OAR-2439).
  const wasThresholdTriggeredRef = useRef(false);
  useEffect(() => {
    if (shouldShowPrompt) {
      wasThresholdTriggeredRef.current =
        !shouldShowLoginPrompt && shouldShowThresholdPrompt;
      // Only the login trigger consumes hasShownThisLogin -- a threshold-only
      // fire must not block the login prompt from showing later this session.
      if (shouldShowLoginPrompt) {
        setHasShownThisLogin(true);
      }
    }
  }, [shouldShowPrompt, shouldShowLoginPrompt, shouldShowThresholdPrompt]);

  // Completing reactivation triggers a full page reload (refresh-lib's payment
  // success/error handlers), which remounts this hook with isAuthenticated
  // already true -- the login-transition above never fires, so the prompt
  // would otherwise never show again without a manual logout/login (OAR-2428
  // QA). Treat exiting reactivation the same as a fresh login.
  const wasInReactivationRef = useRef(isInReactivation);
  useEffect(() => {
    if (wasInReactivationRef.current && !isInReactivation) {
      setJustLoggedIn(true);
      setHasShownThisLogin(false);
    }
    wasInReactivationRef.current = isInReactivation;
  }, [isInReactivation]);

  const markPromptComplete = async () => {
    try {
      await updateSetting({
        setting: 'hide_trustpilot_login_prompt',
        state: true,
      });
    } catch (error) {
      console.error('Failed to update user setting:', error);
      return;
    }

    try {
      await queryClient.invalidateQueries({ queryKey: ['account'] });
    } catch (error) {
      console.error('Failed to invalidate account cache:', error);
    }
  };

  const markReviewLeft = async () => {
    try {
      await updateSetting({
        setting: 'hide_experience_survey_modal',
        state: true,
      });
    } catch (error) {
      console.error('Failed to update user setting:', error);
      return;
    }

    try {
      await queryClient.invalidateQueries({ queryKey: ['account'] });
    } catch (error) {
      console.error('Failed to invalidate account cache:', error);
    }
  };

  return {
    shouldShowPrompt,
    wasThresholdTriggered: wasThresholdTriggeredRef.current,
    markPromptComplete,
    markReviewLeft,
  };
};
